'use client';

import { Button } from '@eliadmin/core/src/components/ui/button';
import { useCarouselAutoplay } from '@eliadmin/core/src/components/ui/use-carousel-autoplay';
import { useCarouselContext } from '@eliadmin/core/src/components/ui/carousel-context';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import useEmblaCarousel, { type UseEmblaCarouselType } from 'embla-carousel-react';
import type { RefObject } from 'react';
import { useEffect, useImperativeHandle, useRef, useState } from 'react';
import { LuChevronDown, LuChevronUp, LuDot, LuMoveLeft, LuMoveRight } from 'react-icons/lu';

type EmblaCarouselType = NonNullable<UseEmblaCarouselType[1]>;
type EmblaOptionsType = NonNullable<Parameters<typeof useEmblaCarousel>[0]>;

type CarouselClassName = {
  wrapper?: string;
  container?: string;
  slide?: string;
  arrows?: string;
  left_arrow?: string;
  right_arrow?: string;
  dots?: string;
  dot?: string;
  customDots?: string;
  customDot?: string;
  main?: string;
  thumbnail?: string;
  thumbnails?: string;
  thumbnailsWrapper?: string;
  /**TODO: dorobit classes */
};

type CarouselCustomDots =
  | {
      showCustomDots: boolean;
      customDots: React.ReactNode[];
      colors: {
        activeColor: string;
        inactiveColor: string;
      };
    }
  | undefined;

export type CarouselProps = {
  className?: CarouselClassName;
  slides: React.ReactNode[];
  /** Lightweight thumbnails; legacy callers keep using their slide content. */
  thumbnails?: React.ReactNode[];
  options?: EmblaOptionsType;
  customDots?: CarouselCustomDots;
  // styles
  showArrows?: boolean;
  customArrowIcons?: { left?: React.ReactNode; right?: React.ReactNode };
  showDots?: boolean;
  showThumbnails?: boolean;
  showThumbnailsArrows?: boolean;
  enableInterval?: boolean;
  intervalLength?: number;
  enableLoop?: boolean;
  openLightbox?: () => void;
  showMoreButton?: boolean;
  allImagesCount?: number;
  emblaRef?: RefObject<EmblaCarouselType | null>;
};

export function Carousel(props: CarouselProps) {
  const { t } = useTranslations();
  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: !!props.enableLoop,
    ...props.options,
  });
  const thumbsRef = useRef<HTMLDivElement | null>(null);
  const dotsRef = useRef<HTMLDivElement | null>(null);
  const interactionRootRef = useRef<HTMLDivElement | null>(null);

  // imperative handle of emblaApi
  useImperativeHandle(props.emblaRef, () => emblaApi!, [emblaApi]);

  const carouselProviderContext = useCarouselContext();

  const [selectedIndex, setSelectedIndex] = useState(0);

  const onPrev = () => emblaApi?.scrollPrev();
  const onNext = () => emblaApi?.scrollNext();

  const isContextDefined = !!carouselProviderContext;

  useCarouselAutoplay(emblaApi, {
    enabled: !!props.enableInterval,
    intervalMs: props.intervalLength || 6000,
    interactionRootRef,
  });

  useEffect(() => {
    if (!emblaApi) return;

    const onSelect = () => {
      setSelectedIndex(emblaApi.selectedScrollSnap());
      if (!isContextDefined) return;
      carouselProviderContext?.setSelectedIndex(emblaApi.selectedScrollSnap());
      carouselProviderContext?.setCanGoNext(emblaApi.canScrollNext());
      carouselProviderContext?.setCanGoPrev(emblaApi.canScrollPrev());
    };

    if (isContextDefined) carouselProviderContext?.setApi(emblaApi);
    emblaApi.on('select', onSelect);
    emblaApi.on('reInit', onSelect);
    onSelect();

    return () => {
      emblaApi.off('select', onSelect);
      emblaApi.off('reInit', onSelect);
      if (isContextDefined) carouselProviderContext?.setApi(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    emblaApi,
    /** IMPORTANT: only check against isContextDefined, because we want to prevent infinite loop */
    isContextDefined,
  ]);

  useEffect(() => {
    if (!isContextDefined) return;
    carouselProviderContext?.setSlideCount(props.slides.length);
    /** IMPORTANT: only check against isContextDefined, because we want to prevent infinite loop */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isContextDefined, props.slides.length]);

  const scrollThumbs = (delta: number) => {
    if (thumbsRef.current) {
      const container = thumbsRef.current;
      const currentScrollTop = container.scrollTop;
      container.scrollTo({
        top:
          delta < 0 && props.options?.loop === true && selectedIndex === 0
            ? container.scrollWidth
            : delta > 0 && props.options?.loop === true && selectedIndex === props.slides.length - 1
              ? 0
              : currentScrollTop + delta,
        behavior: 'smooth',
      });
    }
  };

  const scrollDots = (delta: number) => {
    if (dotsRef.current) {
      const container = dotsRef.current;
      const currentScrollLeft = container.scrollLeft;
      container.scrollTo({
        left:
          delta < 0 && props.options?.loop === true && selectedIndex === 0
            ? container.scrollWidth
            : delta > 0 && props.options?.loop === true && selectedIndex === props.slides.length - 1
              ? 0
              : currentScrollLeft + delta,
        behavior: 'smooth',
      });
    }
  };

  const slides = props.slides.map((slide, index) => {
    return (
      <div
        className={cn(
          'h-full w-full flex-shrink-0 flex-grow-0',
          'ea-carousel__slide',
          props.className?.slide,
        )}
        key={index}
      >
        {slide}
      </div>
    );
  });

  const handleThumbClick = (index: number) => {
    const indexDelta = index - selectedIndex;

    emblaApi?.scrollTo(index);
    scrollDots(indexDelta * 32);
  };

  const thumbs = props.showThumbnails
    ? props.slides.map((slide, index) => {
        return (
          <button
            type="button"
            className={cn(
              'min-h-[48px] w-12 cursor-pointer rounded-sm bg-muted',
              'ea-carousel__thumb',
              index === selectedIndex ? 'border border-foreground' : '',
              props.className?.thumbnail,
            )}
            key={index}
            onClick={() => handleThumbClick(index)}
            aria-label={`${t('text3532')} ${index + 1}`}
            aria-current={index === selectedIndex}
          >
            {props.thumbnails?.[index] ?? slide}
          </button>
        );
      })
    : null;

  return (
    <div
      ref={interactionRootRef}
      className={cn('flex w-full gap-1', 'ea-carousel__wrapper', props.className?.wrapper)}
    >
      {props.showThumbnails && (
        <div
          className={cn(
            'hidden h-full max-h-[500px] min-w-[56px] max-w-[56px] flex-col gap-2 md:flex',
            'ea-carousel__thumbnails',
            props.className?.thumbnailsWrapper,
          )}
        >
          {props.showThumbnailsArrows && (
            <Button
              className={cn(
                'h-5 rounded-sm',
                'ea-carousel__thumbnail-control ea-carousel__thumbnail-control-up',
              )}
              variant="secondary"
              disabled={selectedIndex === 0 && props?.options?.loop === false}
              aria-label={t('text3533')}
              onClick={() => {
                (onPrev(), scrollThumbs(-56));
                scrollDots(-32);
              }}
            >
              <LuChevronUp size={16} aria-hidden="true" />
            </Button>
          )}
          <div
            ref={thumbsRef}
            className={cn(
              'hide-scroll-bar flex min-w-[56px] max-w-[56px] flex-col items-center justify-start gap-2 overflow-hidden rounded-sm',
              'ea-carousel__thumbnail',
              props.className?.thumbnails,
            )}
          >
            {thumbs}
            {props.showMoreButton && (
              <button
                type="button"
                className={cn(
                  'flex min-h-[48px] w-12 cursor-pointer items-center justify-center rounded-lg bg-muted',
                  'ea-carousel__thumb',
                  props.className?.thumbnail,
                  'bg-primary text-background',
                )}
                onClick={() => props.openLightbox?.()}
                aria-label={
                  props.allImagesCount
                    ? `+${props.allImagesCount - 4} ${t('text3534')}`
                    : `Zobraziť viac – ${t('text3534')}`
                }
              >
                {props.allImagesCount ? `+${props.allImagesCount - 4}` : 'Zobraziť viac'}
              </button>
            )}
          </div>
          {props.showThumbnailsArrows && (
            <Button
              className={cn(
                'h-5 rounded-sm',
                'ea-carousel__thumbnail-control ea-carousel__thumbnail-control-down',
              )}
              variant="secondary"
              disabled={selectedIndex === slides.length - 1 && props?.options?.loop === false}
              aria-label={t('text3535')}
              onClick={() => {
                (onNext(), scrollThumbs(56));
                scrollDots(32);
              }}
            >
              <LuChevronDown size={16} aria-hidden="true" />
            </Button>
          )}
        </div>
      )}
      <div
        className={cn('relative overflow-hidden', 'ea-carousel', props.className?.main)}
        ref={emblaRef}
      >
        <div
          className={cn('flex h-full ', 'ea-carousel__container', props.className?.container)}
          onClick={props.openLightbox}
        >
          {slides}
        </div>
        {props.showArrows && (
          <>
            <button
              type="button"
              className={cn(
                'absolute left-2 top-[calc(50%-8px)] rounded-md bg-gray-100 bg-opacity-80 p-1 text-foreground disabled:opacity-50',
                'ea-carousel__control ea-carousel__control--left',
                props.className?.arrows,
                props.className?.left_arrow,
              )}
              disabled={selectedIndex === 0 && props?.options?.loop === false}
              aria-label={t('text3525')}
              onClick={() => {
                (onPrev(), scrollThumbs(-56));
                scrollDots(-32);
              }}
            >
              {props?.customArrowIcons?.left ? (
                props?.customArrowIcons?.left
              ) : (
                <LuMoveLeft className="h-4 w-4" aria-hidden="true" />
              )}
            </button>
            <button
              type="button"
              className={cn(
                'absolute right-2 top-[calc(50%-8px)] rounded-md bg-gray-100 bg-opacity-80 p-1 text-foreground disabled:opacity-50',
                'ea-carousel__control ea-carousel__control--right',
                props.className?.arrows,
                props.className?.right_arrow,
              )}
              disabled={selectedIndex === slides.length - 1 && props?.options?.loop === false}
              aria-label={t('text3526')}
              onClick={() => {
                (onNext(), scrollThumbs(56));
                scrollDots(32);
              }}
            >
              {props?.customArrowIcons?.right ? (
                props?.customArrowIcons?.right
              ) : (
                <LuMoveRight className="h-4 w-4" aria-hidden="true" />
              )}
            </button>
          </>
        )}
        {props.showDots && (
          <div
            className={cn(
              'absolute bottom-4 flex w-full items-center justify-center',
              'ea-carousel__dots__container',
              props.className?.dots,
            )}
          >
            <div
              className={cn(
                'flex max-w-[160px] cursor-pointer justify-start overflow-hidden',
                'ea-carousel__dots__wrapper',
              )}
              ref={dotsRef}
            >
              {slides.map((_, index) => (
                <button
                  key={index}
                  type="button"
                  onClick={() => emblaApi?.scrollTo(index)}
                  aria-label={`${t('text3532')} ${index + 1}`}
                  aria-current={index === selectedIndex}
                >
                  <LuDot
                    className={cn(
                      `h-10 w-10 ${
                        index === selectedIndex ? 'text-foreground' : 'text-muted-foreground'
                      }`,
                      'ea-carousel__dot',
                      props.className?.dot,
                    )}
                    aria-hidden="true"
                  />
                </button>
              ))}
            </div>
          </div>
        )}
        {props.customDots?.showCustomDots && (
          <div
            className={cn(
              'flex w-full items-center justify-center',
              'ea-carousel__custom-dots__container',
              props.className?.customDots,
            )}
          >
            <div
              className={cn('flex w-full cursor-pointer', 'ea-carousel__custom-dots__wrapper')}
              ref={dotsRef}
            >
              {props.customDots.customDots.map((dot, index) => (
                <button
                  key={index}
                  type="button"
                  className={cn(
                    `h-full w-full ${
                      index === selectedIndex
                        ? props.customDots?.colors.activeColor
                        : props.customDots?.colors.inactiveColor
                    }`,
                    props.className?.customDot,
                    'ea-carousel__custom-dot',
                  )}
                  onClick={() => emblaApi?.scrollTo(index)}
                  aria-label={`${t('text3532')} ${index + 1}`}
                  aria-current={index === selectedIndex}
                >
                  <div
                    className={cn(
                      'absolute left-1/2 top-[-8px] border-solid',
                      'ea-carousel__custom-dot-extra',
                    )}
                    style={{
                      content: '""',
                      transform: 'translateX(-50%)',
                      borderWidth: '0 5px 8px 5px',
                      borderColor: `transparent transparent ${
                        index === selectedIndex ? 'hsl(var(--accent))' : 'hsl(var(--secondary))'
                      } transparent`,
                    }}
                  />
                  {dot}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
